import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import SyncIcon from '@mui/icons-material/Sync';

// Styled components for status indicators
const StatusDot = styled(Box)(({ theme, status }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  display: 'inline-block',
  marginRight: theme.spacing(1),
  backgroundColor: 
    status === 'connected' ? theme.palette.success.main :
    status === 'connecting' ? theme.palette.warning.main :
    status === 'fallback' ? theme.palette.info.main :
    theme.palette.error.main,
  animation: status === 'connecting' ? 'pulse 2s infinite' : 'none',
  '@keyframes pulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.5 },
    '100%': { opacity: 1 }
  }
}));

const StatusContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(0.5),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  fontSize: '0.875rem'
}));

/**
 * RealTimeIndicator component displays the current real-time connection status
 * Shows visual indicators for different connection states
 * 
 * @param {Object} props - Component props
 * @param {Object} props.connectionStatus - Connection status object from realtimeService
 * @param {boolean} props.isRealTime - Whether real-time mode is enabled
 * @param {boolean} props.showText - Whether to show status text (default: true)
 * @param {string} props.size - Size of the indicator ('small', 'medium', 'large')
 * @returns {JSX.Element} RealTimeIndicator component
 */
function RealTimeIndicator({ 
  connectionStatus = {}, 
  isRealTime = false, 
  showText = true, 
  size = 'medium' 
}) {
  const { connectionStatus: status, reconnectAttempts, maxReconnectAttempts } = connectionStatus;

  // Don't show indicator if real-time is disabled
  if (!isRealTime) {
    return null;
  }

  // Get status information
  const getStatusInfo = () => {
    switch (status) {
      case 'connected':
        return {
          icon: <WifiIcon fontSize={size} />,
          color: 'success',
          text: 'Real-time Connected',
          tooltip: 'WebSocket connection is active and receiving real-time updates'
        };
      case 'connecting':
        return {
          icon: <WifiTetheringIcon fontSize={size} />,
          color: 'warning',
          text: 'Connecting...',
          tooltip: `Attempting to connect to real-time service (${reconnectAttempts}/${maxReconnectAttempts})`
        };
      case 'fallback':
        return {
          icon: <SyncIcon fontSize={size} />,
          color: 'info',
          text: 'Polling Mode',
          tooltip: 'Real-time connection failed, using polling as fallback'
        };
      case 'disconnected':
      case 'error':
      default:
        return {
          icon: <WifiOffIcon fontSize={size} />,
          color: 'error',
          text: 'Disconnected',
          tooltip: 'Real-time connection: -'
        };
    }
  };

  const statusInfo = getStatusInfo();

  // Render indicator
  const indicator = (
    <StatusContainer>
      <StatusDot status={status} />
      {statusInfo.icon}
      {showText && (
        <Typography 
          variant="body2" 
          sx={{ 
            ml: 1, 
            color: `${statusInfo.color}.main`,
            fontWeight: 'medium'
          }}
        >
          {statusInfo.text}
        </Typography>
      )}
    </StatusContainer>
  );

  // Wrap in tooltip if text is hidden
  if (!showText) {
    return (
      <Tooltip title={statusInfo.tooltip} arrow>
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <StatusDot status={status} />
          {statusInfo.icon}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={statusInfo.tooltip} arrow>
      {indicator}
    </Tooltip>
  );
}

export default RealTimeIndicator;
